import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Footprints, Scan, BarChart3, FileText, CheckCircle, Cpu, Radar } from 'lucide-react';

const ANALYSIS_STEPS = [
  { id: 1, text: 'Inicializando scanner de IA...', icon: Cpu, duration: 2200 },
  { id: 2, text: 'Detectando pontos reflexos...', icon: Radar, duration: 2400 },
  { id: 3, text: 'Mapeando zonas de reatividade...', icon: Footprints, duration: 2400 },
  { id: 4, text: 'Calculando score & protocolo...', icon: BarChart3, duration: 2000 },
  { id: 5, text: 'Gerando mapa personalizado...', icon: FileText, duration: 1000 },
];

const AnalysisLoader = ({ imageUrl, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    const totalDuration = ANALYSIS_STEPS.reduce((sum, step) => sum + step.duration, 0);
    const progressIncrement = 100 / (totalDuration / 100);

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + progressIncrement;
      });
    }, 100);

    const runSteps = async () => {
      for (let i = 0; i < ANALYSIS_STEPS.length; i++) {
        setCurrentStep(i);
        await new Promise((resolve) => setTimeout(resolve, ANALYSIS_STEPS[i].duration));
      }
      setCompleted(true);
      setTimeout(() => onComplete?.(), 800);
    };

    runSteps();

    return () => clearInterval(progressInterval);
  }, [onComplete]);

  const CurrentIcon = ANALYSIS_STEPS[currentStep]?.icon || Scan;

  return (
    <motion.div
      data-testid="analysis-loader"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center px-6"
      style={{
        background:
          'radial-gradient(ellipse at top, #083D3A 0%, #0A0F14 100%)',
      }}
    >
      {/* Particle field */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-cyan-400"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              opacity: [0, 0.8, 0],
              scale: [0, 1.5, 0],
            }}
            transition={{
              duration: 2 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* Top HUD */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="absolute top-8 flex items-center gap-2"
      >
        <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
        <span className="text-cyan-300 text-xs tracking-[0.3em] font-semibold uppercase">
          AJ Scanner v2.0
        </span>
      </motion.div>

      {/* Scanner viewport */}
      <motion.div
        className="relative w-64 h-80 rounded-3xl overflow-hidden mb-8 z-10"
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        style={{
          border: '1px solid rgba(34, 211, 238, 0.4)',
          boxShadow:
            '0 0 60px rgba(34, 211, 238, 0.25), inset 0 0 30px rgba(34, 211, 238, 0.1)',
        }}
      >
        {imageUrl && (
          <img
            src={imageUrl}
            alt="Pé sendo analisado"
            className="w-full h-full object-cover"
          />
        )}

        {/* Dim overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#083D3A]/40" />

        {/* Scanner line */}
        <motion.div
          className="absolute left-0 right-0 h-[3px]"
          style={{
            background:
              'linear-gradient(90deg, transparent, #22D3EE 30%, #D4A256 50%, #22D3EE 70%, transparent)',
            boxShadow: '0 0 20px #22D3EE',
          }}
          animate={{ top: ['0%', '100%', '0%'] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* Grid overlay */}
        <div
          className="absolute inset-0 opacity-50 pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(rgba(34,211,238,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(34,211,238,0.18) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        />

        {/* Corner brackets */}
        {['top-2 left-2', 'top-2 right-2', 'bottom-2 left-2', 'bottom-2 right-2'].map(
          (pos, i) => {
            const corners = {
              0: { borderRight: 'none', borderBottom: 'none', borderRadius: '8px 0 0 0' },
              1: { borderLeft: 'none', borderBottom: 'none', borderRadius: '0 8px 0 0' },
              2: { borderRight: 'none', borderTop: 'none', borderRadius: '0 0 0 8px' },
              3: { borderLeft: 'none', borderTop: 'none', borderRadius: '0 0 8px 0' },
            };
            return (
              <div
                key={i}
                className={`absolute ${pos} w-7 h-7`}
                style={{
                  border: '3px solid #22D3EE',
                  boxShadow: '0 0 8px rgba(34,211,238,0.6)',
                  ...corners[i],
                }}
              />
            );
          }
        )}

        {/* Detected dots */}
        {[
          { top: '25%', left: '35%', delay: 0 },
          { top: '50%', left: '55%', delay: 0.3 },
          { top: '65%', left: '40%', delay: 0.6 },
          { top: '35%', left: '60%', delay: 0.9 },
        ].map((dot, i) => (
          <motion.div
            key={i}
            className="absolute w-3 h-3 rounded-full"
            style={{
              top: dot.top,
              left: dot.left,
              background: '#D4A256',
              boxShadow: '0 0 14px #D4A256',
            }}
            animate={{ scale: [1, 1.7, 1], opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 1.4, repeat: Infinity, delay: dot.delay }}
          />
        ))}
      </motion.div>

      {/* Step indicator */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.3 }}
          className="flex items-center gap-3 mb-6 relative z-10"
        >
          <motion.div
            className="w-11 h-11 rounded-full flex items-center justify-center"
            style={{
              background: 'rgba(34, 211, 238, 0.12)',
              border: '1px solid rgba(34, 211, 238, 0.35)',
              boxShadow: '0 0 20px rgba(34, 211, 238, 0.3)',
            }}
            animate={{ rotate: completed ? 0 : 360 }}
            transition={{ duration: 2, repeat: completed ? 0 : Infinity, ease: 'linear' }}
          >
            {completed ? (
              <CheckCircle className="w-5 h-5 text-emerald-400" />
            ) : (
              <CurrentIcon className="w-5 h-5 text-cyan-300" />
            )}
          </motion.div>
          <span className="text-white text-base md:text-lg font-medium">
            {completed ? 'Análise completa.' : ANALYSIS_STEPS[currentStep]?.text}
          </span>
        </motion.div>
      </AnimatePresence>

      {/* Progress bar */}
      <div className="w-full max-w-xs relative z-10">
        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{
              background:
                'linear-gradient(90deg, #0F766E, #22D3EE, #D4A256)',
              boxShadow: '0 0 12px rgba(34,211,238,0.6)',
            }}
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.1 }}
          />
        </div>
        <div className="flex justify-between mt-2 text-white/50 text-xs tracking-wider">
          <span className="uppercase">Processando IA</span>
          <span className="font-mono text-cyan-300">{Math.round(progress)}%</span>
        </div>
      </div>

      {/* Steps dots */}
      <div className="flex gap-1.5 mt-6 relative z-10">
        {ANALYSIS_STEPS.map((step, index) => (
          <motion.div
            key={step.id}
            className="w-1.5 h-1.5 rounded-full"
            style={{
              background: index <= currentStep ? '#22D3EE' : 'rgba(255,255,255,0.2)',
              boxShadow: index <= currentStep ? '0 0 8px #22D3EE' : 'none',
            }}
            animate={index === currentStep && !completed ? { scale: [1, 1.4, 1] } : {}}
            transition={{ duration: 0.6, repeat: Infinity }}
          />
        ))}
      </div>
    </motion.div>
  );
};

export default AnalysisLoader;
